import { ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';

import { useMoTaHanMuc, type MoTaHanMuc } from '../../hooks/useMoTaHanMuc';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';
import { tachLoiHanMuc, type ThongTinHanMuc } from '../../lib/loi-han-muc';

export interface ThongBaoHetHanMucProps {
  thongTin: ThongTinHanMuc;
  /**
   * - `khoi`: dải đầy đủ ở đầu màn.
   * - `gon`: trong bong bóng chat, thẻ hẹp, hộp thoại.
   * - `o`: ô bảng, hai ba dòng chữ nhỏ; phần còn lại ở `title`.
   */
  bienThe?: 'khoi' | 'gon' | 'o';
  /** Nhãn nút làm lại của màn, nếu màn có. Có thì hiện dòng "Khi có thêm hạn mức, hãy bấm ...". */
  nhanThuLai?: string;
  /** Chính nút đó, đặt cạnh dòng trên. Nút nằm chỗ khác trên màn thì chỉ truyền `nhanThuLai`. */
  nutThuLai?: ReactNode;
  className?: string;
}

/** Tên nút không ngắt giữa chừng: "Thử lại xử lý", "Re-score" đọc liền một cụm. */
const giuNhanLien = (nhan: string) => nhan.replace(/ /g, '\u00a0').replace(/-/g, '\u2011');

function ThanhDaDung({ moTa, nho }: { moTa: MoTaHanMuc; nho?: boolean }) {
  if (!moTa.daDung) return null;
  return (
    <div className={cn('max-w-md space-y-1.5', nho && 'space-y-1')}>
      {moTa.phanTram !== null && (
        <div aria-hidden className="h-1.5 overflow-hidden rounded-full bg-warning-100">
          <div className="h-full rounded-full bg-warning-500" style={{ width: `${moTa.phanTram}%` }} />
        </div>
      )}
      <p className={cn('tabular-nums text-content-primary', nho ? 'text-xs' : 'text-sm')}>{moTa.daDung}</p>
    </div>
  );
}

/**
 * Thông báo hết hạn mức AI, tách dòng theo mức quan trọng thay cho một khối chữ liền. Nhận kết quả của `tachLoiHanMuc`;
 * tách không được thì nơi gọi giữ cách hiện câu gốc của nó.
 *
 * Tông `warning` chứ không `danger`: đây là giới hạn đang có hiệu lực và tự mở lại, không phải hệ thống hỏng.
 */
export function ThongBaoHetHanMuc({ thongTin, bienThe = 'khoi', nhanThuLai, nutThuLai, className }: ThongBaoHetHanMucProps) {
  const { t } = useT();
  const { moTa: dung, dongGon } = useMoTaHanMuc();
  const moTa = dung(thongTin);
  const ma = thongTin.maLoi;

  if (bienThe === 'o') {
    const day = [dongGon(thongTin), ...moTa.ghiChu, moTa.khacVanDung, moTa.huongDan, thongTin.boQuaTieuDe ? t('hanMucAi.boQuaTieuDeMo') : null,
      ma ? t('hanMucAi.maLoi', { ma }) : null].filter(Boolean).join(' ');
    return (
      <span title={day} className={cn('flex min-w-0 flex-col gap-0.5 break-words text-2xs', className)}>
        {moTa.boQua && <span className="text-content-secondary">{moTa.boQua}</span>}
        <span className="text-balance font-medium text-warning-700">{moTa.tieuDe}</span>
        {moTa.dongO && <span className="tabular-nums text-content-muted">{moTa.dongO}</span>}
      </span>
    );
  }

  const gon = bienThe === 'gon';
  const coSo = moTa.daDung || moTa.can || moTa.moLai || moTa.tinhNang;
  const coLoi = moTa.ghiChu.length > 0 || moTa.khacVanDung || moTa.huongDan || thongTin.boQuaTieuDe;
  const chuPhu = gon ? 'text-xs' : 'text-sm';

  return (
    <div
      role="alert"
      title={gon && ma ? t('hanMucAi.maLoi', { ma }) : undefined}
      className={cn('rounded-lg border border-warning-100 bg-warning-50', gon ? 'p-3' : 'px-5 py-4', className)}
    >
      <div className={cn('flex items-start', gon ? 'gap-2' : 'gap-3')}>
        <WarningOutlined aria-hidden className={cn('shrink-0 text-warning-600', gon ? 'mt-0.5 text-sm' : 'mt-0.5 text-base')} />
        <div className={cn('min-w-0 flex-1', gon ? 'space-y-2' : 'space-y-3')}>
          <div className="space-y-0.5">
            {moTa.boQua && <p className={cn(chuPhu, 'text-content-secondary')}>{moTa.boQua}</p>}
            <p className={cn('text-balance font-semibold text-content-primary', gon ? 'text-sm' : 'text-base')}>{moTa.tieuDe}</p>
            {moTa.tinhNang && <p className={cn(chuPhu, 'break-words text-content-secondary')}>{moTa.tinhNang}</p>}
          </div>

          {coSo && (
            <div className={gon ? 'space-y-1.5' : 'space-y-2'}>
              <ThanhDaDung moTa={moTa} nho={gon} />
              {moTa.can && <p className={cn(chuPhu, 'tabular-nums font-medium text-content-primary')}>{moTa.can}</p>}
              {moTa.con && <p className={cn(chuPhu, 'tabular-nums text-content-primary')}>{moTa.con}</p>}
              {moTa.moLai && (
                <p className={cn(chuPhu, 'flex items-start gap-1.5 tabular-nums text-content-primary')}>
                  <ClockCircleOutlined aria-hidden className="mt-0.5 shrink-0 text-xs text-content-muted" />
                  <span className="min-w-0">{moTa.moLai}</span>
                </p>
              )}
            </div>
          )}

          {coLoi && (
            <div className="space-y-1">
              {moTa.ghiChu.map((c) => <p key={c} className={cn(chuPhu, 'text-content-secondary')}>{c}</p>)}
              {thongTin.boQuaTieuDe && <p className={cn(chuPhu, 'text-content-secondary')}>{t('hanMucAi.boQuaTieuDeMo')}</p>}
              {moTa.khacVanDung && <p className="text-xs text-content-secondary">{moTa.khacVanDung}</p>}
              {moTa.huongDan && <p className={cn(chuPhu, 'text-content-secondary')}>{moTa.huongDan}</p>}
            </div>
          )}

          {nhanThuLai && (
            <div className={cn('flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-warning-100', gon ? 'pt-2' : 'pt-3')}>
              <p className={cn('min-w-0 flex-1 basis-56', chuPhu, 'text-content-secondary')}>{t('hanMucAi.khiCoThem', { nut: giuNhanLien(nhanThuLai) })}</p>
              {nutThuLai}
            </div>
          )}

          {!gon && ma && <p className="font-mono text-2xs text-content-muted">{t('hanMucAi.maLoi', { ma })}</p>}
        </div>
      </div>
    </div>
  );
}

/** Cho nơi chỉ có câu chữ: tách được thì hiện thông báo, không thì hiện `children`, tức cách hiện câu gốc của màn đó. */
export function HanMucHoacCauGoc({
  cau, maLoi, children, ...phan
}: Omit<ThongBaoHetHanMucProps, 'thongTin'> & { cau: string | null | undefined; maLoi?: string | null; children: ReactNode }) {
  const thongTin = tachLoiHanMuc(cau, maLoi);
  return thongTin ? <ThongBaoHetHanMuc {...phan} thongTin={thongTin} /> : <>{children}</>;
}
